import { memo, useMemo } from 'react';
import type { ReactNode } from 'react';
import { VStack } from '@library';
import useProductsListControls from '../../../hooks/useProductsListControls';
import useProductsListPage from '../../../queries/useProductsListPage';
import usePriceRange from '../../../queries/usePriceRange';
import useTabletView from '../../../../core/hooks/dom/useTabletView';
import Container from '../../../../core/components/Container';
import CategoriesTree from '../Filters/CategoriesTree';
import AttributeFilter from '../../ProductFilters/AttributeFilter';
import PageHeader from '../Header/PageHeader';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import type { ExternalFilters } from '../context/ProductListOptionsProvider';
import ProductListOptionsProvider from '../context/ProductListOptionsProvider';
import useCategoryTree from '../../../queries/useCategoryTree';
import { getCategoriesDescription } from '../../../util/getCategoriesDescription';
import styles from './ProductList.module.scss';
import ProductListBody from './ProductListBody';

interface ProductListProps {
    ignoreList: string[];
    featuredFilter?: ReactNode;
    header?: ReactNode;
    subHeader?: ReactNode;
    banner?: ReactNode;
    outerLoading?: boolean;
    rootLinks?: {
        text?: string;
        messageId?: string;
        to?: string;
    }[];
    singleRootCategory?: boolean;
    showEmptyCategories?: boolean;
    isSearchResultPage?: boolean;
    isSalePage?: boolean;
    headerAction?: ReactNode;
    topBanner?: ReactNode;
    title?: string;
}

const ProductListApi = memo(
    ({
        ignoreList,
        featuredFilter,
        header,
        subHeader,
        banner,
        topBanner,
        outerLoading,
        rootLinks,
        singleRootCategory,
        showEmptyCategories,
        isSearchResultPage,
        isSalePage,
        headerAction,
        title,
    }: Omit<ProductListProps, 'filters'>) => {
        const [pageSize] = useProductsListControls();

        const {
            products,
            categories,
            total,
            filteredTotal,
            totalLoading,
            categoryCountsLoading,
            loading,
        } = useProductsListPage({
            rootTree: !singleRootCategory,
        });

        const priceRange = usePriceRange();
        const isTabletView = useTabletView();

        const {
            categoryId,
            loading: loadingTree,
            tree,
        } = useCategoryTree({
            categories,
            showEmptyCategories,
        });

        const description = useMemo(() => {
            return getCategoriesDescription(tree, categoryId)
                .map(category => category.name)
                .join(' | ');
        }, [tree, categoryId]);

        return (
            <div className={styles.searchResultsRoot}>
                <VStack>
                    <PageHeader
                        rootLinks={rootLinks}
                        description={description}
                        title={title}
                    />
                    <div>
                        <Container>{topBanner}</Container>
                        <ProductListBody
                            ignoreList={ignoreList}
                            sidebar={
                                <div>
                                    {featuredFilter && (
                                        <div className={styles.featuredFilter}>
                                            {featuredFilter}
                                        </div>
                                    )}
                                    <div className={styles.categoriesFilter}>
                                        <CategoriesTree
                                            tree={tree}
                                            loading={loadingTree}
                                            categoryId={categoryId}
                                            paramIgnoreList={ignoreList}
                                            singleRootCategory={
                                                singleRootCategory
                                            }
                                            notRedirectToRoot={
                                                isSearchResultPage
                                            }
                                            countsLoading={
                                                categoryCountsLoading
                                            }
                                        />
                                    </div>
                                    <AttributeFilter
                                        defaultExpand={!isTabletView}
                                        priceRange={priceRange}
                                        ignoreList={ignoreList}
                                        isSearchResultPage={isSearchResultPage}
                                        salePage={isSalePage}
                                    />
                                </div>
                            }
                            header={header}
                            subHeader={subHeader}
                            headerAction={headerAction}
                            banner={banner}
                            items={products}
                            loading={loading || outerLoading}
                            total={total}
                            filteredTotal={filteredTotal}
                            totalLoading={totalLoading}
                            pageSize={pageSize}
                        />
                    </div>
                </VStack>
            </div>
        );
    }
);

const ProductList = ({
    isFiltersLoading,
    filters,
    ...props
}: ProductListProps & {
    filters?: ExternalFilters;
    isFiltersLoading?: boolean;
}) => {
    return (
        <ProductListOptionsProvider
            isFiltersLoading={isFiltersLoading}
            externalFilters={filters}
        >
            <ProductListApi {...props} />
        </ProductListOptionsProvider>
    );
};

export default memo(withErrorBoundary(ProductList));
